import { FC, useContext } from 'react';

import { useContent } from '@eneco/cms';
import { getStaticPrefix } from '@eneco/common/env';
import CustomerIdInput from '@eneco/components/CustomerIdInput/CustomerIdInput';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { Button, Form, Heading, NavLink, Stack, Text, TextLink, Image } from '@eneco/sparky';
import { ChevronLeftIcon, InfoIcon } from '@eneco/sparky/icons';
import { TrackViewComponent } from '@eneco/tracking';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { RegistrationAccountContent } from '../registrationFlow.types';
import { canRegisterCustomerIDSchema } from './canRegisterCustomerIDSchema';
import { Action, State } from '../registrationFlow.constants';
import { RegistrationMachine } from '../registrationFlow.machine';

const resolver = typedYupResolver<FormValues>(canRegisterCustomerIDSchema);

type FormValues = {
  customerId: number;
};

export const CanRegisterCustomerID: FC = () => {
  const { label } = useDC();
  const content = useContent<RegistrationAccountContent>();
  const { send, state } = useContext(RegistrationMachine);
  const isValidatingCustomer = state.value === State.VALIDATING_CUSTOMER;
  const { isInvalidCombination } = state.context;
  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<FormValues>({
    defaultValues: {
      customerId: state.context.customerId,
    },
    resolver,
  });
  const handleBack = () => send({ type: Action.BACK });

  const submitForm: SubmitHandler<FormValues> = ({ customerId }) => {
    send({ type: Action.CAN_REGISTER_CONTINUE_CUSTOMER_ID, customerId });
  };

  const formErrorMessages: { [key: string]: string } = {
    customerIdRequired: content.customerIdFormField?.requiredMessage ?? '',
    customerIdInvalid: content.customerIdFormField?.validationMessage ?? '',
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <Stack gap="6">
        <NavLink variant="secondary" onClick={handleBack} leftIcon={<ChevronLeftIcon />}>
          {content.CanRegisterCustomerID_backLinkText}
        </NavLink>
        <Heading as="h1" size="XS">
          {content.CanRegisterCustomerID_title}
        </Heading>
        {isInvalidCombination && (
          <TrackedNotificationBox
            label={content.errorNotificationField?.content}
            isAlert={true}
            text={<RichText html={content.errorNotificationField?.content} />}
            variant="error"
          />
        )}
        <Stack gap="3">
          <Text>{content.CanRegisterCustomerID_introductionText}</Text>
          <TrackedDialog
            trigger={
              <TextLink rightIcon={<InfoIcon size="small" />}>
                {content.CanRegisterCustomerID_modalDialog?.triggerText}
              </TextLink>
            }
            title={content.CanRegisterCustomerID_modalDialog?.title}>
            <TrackViewComponent type="dialog" label={content.CanRegisterCustomerID_modalDialog?.title ?? ''}>
              <Stack gap="4">
                <Stack.Item>
                  <RichText html={content.inEmailModalDescription} />
                </Stack.Item>
                <Stack.Item>
                  <Image
                    src={`${getStaticPrefix()}/images/${label}/registration/id_in_email.svg`}
                    alt={content.inEmailModalLabelText ?? ''}
                  />
                </Stack.Item>
                <Stack.Item>
                  <RichText html={content.onBankStatementModalDescription} />
                </Stack.Item>
                <Stack.Item>
                  <Image
                    src={`${getStaticPrefix()}/images/${label}/registration/id_on_bank_statement.svg`}
                    alt={content.onBankStatementModalLabelText ?? ''}
                  />
                </Stack.Item>
                <Stack.Item>
                  <RichText html={content.onYearnoteModalDescription} />
                </Stack.Item>
                <Stack.Item>
                  <Image
                    src={`${getStaticPrefix()}/images/${label}/registration/id_on_yearnote.svg`}
                    alt={content.onYearnoteModalLabelText ?? ''}
                  />
                </Stack.Item>
              </Stack>
            </TrackViewComponent>
          </TrackedDialog>
        </Stack>
        <Controller
          control={control}
          name="customerId"
          render={({ field: { onChange, value, name } }) => (
            <CustomerIdInput
              error={errors?.customerId?.message && formErrorMessages[errors.customerId.message]}
              label={content.customerIdFormField?.label ?? ''}
              name={name}
              onChange={onChange}
              value={value || ''}
            />
          )}
        />
        <Button type="submit" isLoading={isValidatingCustomer}>
          {content.CanRegisterCustomerID_continueButtonText}
        </Button>
      </Stack>
    </Form>
  );
};
